import { Typography, Form } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import i18n from 'i18next';

import { saveContactInfo } from 'features/registration/actions';
import { getUserProfile } from 'features/user/actions';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import {
	FormItem,
	SelectField,
	SubmitButton,
	TextField,
	PhoneField,
	DateField,
} from 'componets/form';
import { Paper } from 'componets/ui';
import { RegistrationLayout } from 'componets/layouts';

import { ClientRole, Rules, Paths, RequestStatus, ACCESS_TOKEN } from 'utils/constants';
import Utils from 'utils/utils';
import { getInStorage } from 'utils';
import { logout } from 'features/authorization/actions';
import { FieldData, ContactInfoCompanyProps, ContactInfoUserProps } from 'models';
import { selectCountries, selectUser } from 'selectors';

import { SettingsDeleteAccount } from '../../Account/Settings/SettingsDeleteAccount';

import styles from '../index.module.scss';

const MIN_AGE = 18;

const GENDERS = [
	{ value: 'Male', label: i18n.t('maleText') },
	{ value: 'Female', label: i18n.t('femaleText') },
];

export const PersonalInformation: FC = () => {
	const t = useTranslate([
		'personalInformationFormText',
		'companyText',
		'companyRegistrationNumberText',
		'firstNameText',
		'lastNameText',
		'dateOfBirthText',
		'genderText',
		'countryText',
		'cityText',
		'addressLabelText',
		'zipCodeText',
		'continueText',
		'selectCountryText',
		'selectGenderText',
		'referralCodeText',
		'settingsDeleteAccountText',
	]);

	const navigate = useNavigate();
	const [form] = Form.useForm();

	const [fields, setFields] = useState<FieldData[]>([]);

	const status = useAppSelector((state) => state.registration.status);
	const user = useAppSelector(selectUser);
	const countries = useAppSelector(selectCountries);

	const isCompany = user.userType === ClientRole.COMPANY;

	const dispatch = useAppDispatch();

	useEffect(() => {
		if (!user?.status && Cookies.get(ACCESS_TOKEN)) {
			dispatch(getUserProfile());
		}
	}, []);

	const onFinish = (data: ContactInfoCompanyProps | ContactInfoUserProps) => {
		const countryId = countries?.find((item) => item.iso === data.countryId)?.id || '';

		dispatch(
			saveContactInfo({
				...data,
				countryId,
				referralCode: data.referralCode
					? data.referralCode
					: typeof getInStorage('referralCode') === 'string'
					? JSON.parse(getInStorage('referralCode') || '')
					: '',
			}),
		)
			.unwrap()
			.then(() => {
				navigate(Paths.VERIFY2FA);
			})
			.catch(({ errors }) => {
				Utils.injectFormErrors(fields, errors, setFields);

				if (errors[0]?.code === 409) {
					dispatch(logout());
				}
				Utils.scrollToFirstError(errors, form);
			});
	};

	const countriesOptions = useMemo(
		() =>
			countries
				?.filter((item) => item.activeForRegistration)
				.map((item: { label: string; iso: string }) => {
					return {
						label: item.label,
						value: item.iso,
					};
				}) || [],
		[countries],
	);

	const maxDate = moment().subtract(MIN_AGE, 'year');

	const country = Form.useWatch('countryId', form);

	return (
		<RegistrationLayout email={user.email}>
			<Paper styleName={styles.blockForm} top={<>2 / 2</>}>
				<Typography.Title level={4} className={styles.contactInfo}>
					{t.personalInformationFormText}
				</Typography.Title>

				<Form
					scrollToFirstError={{
						behavior: 'smooth',
						block: 'center',
					}}
					onFinish={onFinish}
					onFieldsChange={setFields}
					fields={fields}
					form={form}
					initialValues={{
						referralCode:
							typeof getInStorage('referralCode') === 'string'
								? JSON.parse(getInStorage('referralCode') || '')
								: '',
					}}
				>
					{isCompany && (
						<>
							<FormItem
								name="companyName"
								rules={[Rules.REQUIRED, Rules.MAX_LENGTH('companyName', 100)]}
							>
								<TextField label={t.companyText} />
							</FormItem>
							<FormItem name="idNumber" rules={[Rules.REQUIRED, Rules.MAX_LENGTH('idNumber', 255)]}>
								<TextField label={t.companyRegistrationNumberText} />
							</FormItem>
						</>
					)}

					<FormItem name="firstName" rules={[Rules.REQUIRED, Rules.MAX_LENGTH('firstName', 100)]}>
						<TextField label={t.firstNameText} />
					</FormItem>

					<FormItem name="lastName" rules={[Rules.REQUIRED, Rules.MAX_LENGTH('lastName', 100)]}>
						<TextField label={t.lastNameText} />
					</FormItem>

					<FormItem
						name="dateOfBirth"
						rules={[Rules.REQUIRED, Rules.INVALID_DATE()]}
						className="dateField"
					>
						<DateField maxDate={maxDate} label={t.dateOfBirthText} />
					</FormItem>

					<FormItem className="selectField" name="gender" rules={[Rules.REQUIRED]}>
						<SelectField
							options={GENDERS}
							placeholder={t.genderText}
							selectText={t.selectGenderText}
						/>
					</FormItem>

					<FormItem name="countryId" rules={[Rules.REQUIRED]} className="selectField">
						<SelectField
							selectText={t.selectCountryText}
							options={countriesOptions}
							placeholder={t.countryText}
							onChange={(v: string) => {
								const country = countries?.find((item: { id: any }) => item.id === v)?.iso;

								form.setFieldValue('countryId', country);
							}}
							isFilter
						/>
					</FormItem>

					<FormItem name="city" rules={[Rules.REQUIRED, Rules.MAX_LENGTH('city', 50)]}>
						<TextField label={t.cityText} />
					</FormItem>

					<FormItem name="address" rules={[Rules.REQUIRED, Rules.MAX_LENGTH('address', 255)]}>
						<TextField label={t.addressLabelText} />
					</FormItem>

					<FormItem name="zip" rules={[Rules.REQUIRED, Rules.MAX_LENGTH('zip', 20)]}>
						<TextField label={t.zipCodeText} />
					</FormItem>

					<FormItem className="phone" name="phone" rules={[Rules.REQUIRED, Rules.PHONE('phone')]}>
						<PhoneField country={country} />
					</FormItem>

					<FormItem name="referralCode" rules={[Rules.MAX_LENGTH('referralCode', 255)]}>
						<TextField label={t.referralCodeText} />
					</FormItem>

					<SubmitButton text={t.continueText} loading={status === RequestStatus.PENDING} />
				</Form>
			</Paper>

			<div className={styles.delete}>
				<SettingsDeleteAccount />
			</div>
		</RegistrationLayout>
	);
};
